class UpsellColorVariantSync {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId || null;
    this.colorIndex = this.parseColorIndex(root);
    this.variants = null;
    this.cards = [];
    this.cardDefaults = new WeakMap();
    this.cardMaps = new WeakMap();
    this.cardSet = new WeakSet();

    this.onVariantChange = this.onVariantChange.bind(this);

    if (this.colorIndex == null) {
      this.colorIndex = this.getColorIndexFromDom();
    }

    this.refreshCards();
    this.observeRecommendations();
    this.subscribeToVariantChanges();
    this.applyFromCurrent();
  }

  parseColorIndex(root) {
    const raw = root.dataset.pdpColorIndex;
    const idx = Number.parseInt(raw, 10);
    return Number.isFinite(idx) ? idx : null;
  }

  normalize(val) {
    return (val || '').toString().trim().toLowerCase();
  }

  isColorLabel(label) {
    const text = this.normalize(label);
    return text.includes('color') || text.includes('colour') || text.includes('kleur');
  }

  getColorIndexFromDom() {
    const section = this.root.closest('.shopify-section');
    const variantSelects = section?.querySelector('variant-selects');
    if (!variantSelects) return null;

    const optionGroups = Array.from(
      variantSelects.querySelectorAll(
        '.product-form__input--swatch, .product-form__input--pill, .product-form__input--dropdown'
      )
    );

    for (let i = 0; i < optionGroups.length; i += 1) {
      const group = optionGroups[i];
      const legend = group.querySelector('legend');
      const label = group.querySelector('label');
      const name = legend?.childNodes?.[0]?.textContent || legend?.textContent || label?.textContent || '';
      const clean = name.replace(':', '').trim();
      if (this.isColorLabel(clean)) return i;
    }

    return null;
  }

  getSelectedColorFromDom() {
    const section = this.root.closest('.shopify-section');
    const variantSelects = section?.querySelector('variant-selects');
    if (!variantSelects) return '';

    const optionGroups = Array.from(
      variantSelects.querySelectorAll(
        '.product-form__input--swatch, .product-form__input--pill, .product-form__input--dropdown'
      )
    );

    for (const group of optionGroups) {
      const legend = group.querySelector('legend');
      const label = group.querySelector('label');
      const name = legend?.childNodes?.[0]?.textContent || legend?.textContent || label?.textContent || '';
      const clean = name.replace(':', '').trim();
      if (!this.isColorLabel(clean)) continue;

      const select = group.querySelector('select');
      if (select && select.value) return this.normalize(select.value);

      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked?.value) return this.normalize(checked.value);

      const selectedValue = group.querySelector('[data-selected-value]');
      if (selectedValue?.textContent) return this.normalize(selectedValue.textContent);
    }

    return '';
  }

  getVariantOptions(variant) {
    if (!variant) return [];
    if (Array.isArray(variant.options)) return variant.options;
    return [variant.option1, variant.option2, variant.option3].filter((opt) => opt != null);
  }

  getVariants() {
    if (this.variants) return this.variants;
    const section = this.root.closest('.shopify-section');
    if (!section) return [];
    const variantsEl = section.querySelector('script[id^="ProductVariants-"]');
    if (!variantsEl) return [];
    try {
      const data = JSON.parse(variantsEl.textContent);
      this.variants = Array.isArray(data) ? data : [];
    } catch (e) {
      this.variants = [];
    }
    return this.variants;
  }

  getCurrentColorKey() {
    if (this.colorIndex == null) return this.getSelectedColorFromDom();
    const section = this.root.closest('.shopify-section');
    const form = section?.querySelector('form[action^="/cart/add"]');
    const idEl = form?.querySelector('input[name="id"], select[name="id"]');
    const variantId = idEl?.value;
    if (!variantId) return this.getSelectedColorFromDom();

    const variants = this.getVariants();
    const variant = variants.find((v) => String(v.id) === String(variantId));
    if (!variant) return this.getSelectedColorFromDom();
    const options = this.getVariantOptions(variant);
    return this.normalize(options[this.colorIndex]);
  }

  parseColorMap(card) {
    const raw = card.dataset.upsellColorMap || '';
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  cacheDefaults(card) {
    if (this.cardDefaults.has(card)) return;
    const links = Array.from(card.querySelectorAll('a[href]')).map((el) => ({
      el,
      href: el.getAttribute('href'),
    }));
    const buttons = Array.from(card.querySelectorAll('button[data-product-url]')).map((el) => ({
      el,
      dataProductUrl: el.getAttribute('data-product-url'),
    }));
    const dataUrls = Array.from(card.querySelectorAll('[data-url]')).map((el) => ({
      el,
      dataUrl: el.getAttribute('data-url'),
    }));
    const images = Array.from(card.querySelectorAll('.card__media img')).map((el) => ({
      el,
      src: el.getAttribute('src'),
      srcset: el.getAttribute('srcset'),
      width: el.getAttribute('width'),
      height: el.getAttribute('height'),
      alt: el.getAttribute('alt'),
    }));

    this.cardDefaults.set(card, { links, buttons, dataUrls, images });
  }

  refreshCards() {
    const cards = Array.from(this.root.querySelectorAll('[data-upsell-color-map]'));
    cards.forEach((card) => {
      if (this.cardSet.has(card)) return;
      const map = this.parseColorMap(card);
      this.cardMaps.set(card, map);
      this.cacheDefaults(card);
      this.cardSet.add(card);
      this.cards.push(card);
    });
  }

  updateCard(card, entry) {
    const defaults = this.cardDefaults.get(card);
    if (!defaults) return;

    const href = entry?.url || null;
    if (href) {
      defaults.links.forEach((link) => {
        link.el.setAttribute('href', href);
      });
      defaults.buttons.forEach((button) => {
        button.el.setAttribute('data-product-url', href);
      });
      defaults.dataUrls.forEach((item) => {
        item.el.setAttribute('data-url', href);
      });
    }

    if (entry?.src) {
      defaults.images.forEach((img) => {
        img.el.setAttribute('src', entry.src);
        if (entry.srcset) {
          img.el.setAttribute('srcset', entry.srcset);
        } else {
          img.el.removeAttribute('srcset');
        }
        if (entry.width) img.el.setAttribute('width', entry.width);
        if (entry.height) img.el.setAttribute('height', entry.height);
        if (entry.alt) img.el.setAttribute('alt', entry.alt);
      });
    }
  }

  restoreCard(card) {
    const defaults = this.cardDefaults.get(card);
    if (!defaults) return;

    defaults.links.forEach((link) => {
      if (link.href) link.el.setAttribute('href', link.href);
    });
    defaults.buttons.forEach((button) => {
      if (button.dataProductUrl) button.el.setAttribute('data-product-url', button.dataProductUrl);
    });
    defaults.dataUrls.forEach((item) => {
      if (item.dataUrl) item.el.setAttribute('data-url', item.dataUrl);
    });

    defaults.images.forEach((img) => {
      if (img.src) img.el.setAttribute('src', img.src);
      if (img.srcset) {
        img.el.setAttribute('srcset', img.srcset);
      } else {
        img.el.removeAttribute('srcset');
      }
      if (img.width) img.el.setAttribute('width', img.width);
      if (img.height) img.el.setAttribute('height', img.height);
      if (img.alt) img.el.setAttribute('alt', img.alt);
    });
  }

  applyColor(colorKey) {
    const normalized = this.normalize(colorKey);

    this.cards.forEach((card) => {
      const map = this.cardMaps.get(card) || [];
      const match = map.find((item) => this.normalize(item.color) === normalized);
      if (match) {
        this.updateCard(card, match);
      } else {
        this.restoreCard(card);
      }
    });
  }

  applyFromCurrent() {
    const colorKey = this.getCurrentColorKey();
    if (colorKey) {
      this.applyColor(colorKey);
    } else {
      this.cards.forEach((card) => this.restoreCard(card));
    }
  }

  onVariantChange(event) {
    const source = event?.data?.source;
    if (source?.closest?.('quick-add-modal')) return;
    const eventSectionId = event?.data?.sectionId;
    if (eventSectionId && this.sectionId && eventSectionId !== this.sectionId) {
      return;
    }
    if (this.colorIndex == null) {
      this.applyFromCurrent();
      return;
    }
    const variant = event?.data?.variant;
    if (!variant) {
      this.applyFromCurrent();
      return;
    }
    const options = this.getVariantOptions(variant);
    const colorKey = this.normalize(options[this.colorIndex]);
    if (!colorKey) {
      this.cards.forEach((card) => this.restoreCard(card));
      return;
    }
    this.applyColor(colorKey);
  }

  subscribeToVariantChanges() {
    if (typeof subscribe !== 'function') return;
    if (typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) return;
    subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
  }

  observeRecommendations() {
    const observer = new MutationObserver(() => {
      this.refreshCards();
      this.applyFromCurrent();
    });
    observer.observe(this.root, { childList: true, subtree: true });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll('product-recommendations.complementary-products')
    .forEach((el) => new UpsellColorVariantSync(el));
});
